import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import PropTypes, { string } from "prop-types";
import styles from "./SelectField.module.css";
import { getChildrenCommonCode, useFetch } from "@maxis/client-logic";
import { getValueByKey, normalizeArrayData } from "@maxis/client-logic/utils/dataShapeUtil";

/**
 * SelectField
 * @type React.FC<SelectFieldPropTypes>
 */
const SelectField = React.forwardRef((props, ref) => {
  const { request } = useFetch();
  const refSelect = useRef();
  const callbackRef = useRef();

  const [currState, setCurrState] = useState({
    selectedIndex: 0,
    selectedValue: "",
    selectedData: null,
    placeholder: props.placeholder,
    isSearchable: props.isSearchable,
    isClearable: props.isClearable,
    disabled: props.disabled,
    dataSource: [],
    options: [],
  });

  Object.keys(props).forEach((p) => {
    if (!Object.keys(SelectFieldPropTypes).includes(p)) {
      console.error(`SelectField: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /**
     * value(값) 리턴
     * @returns
     */
    getValue() {
      const data = currState.selectedValue;
      if (!data || Object.keys(data).length === 0) return "";

      if (props.isMulti) {
        if (data?.["value"]) {
          return data?.["value"];
        } else {
          return data?.map((x) => x.value).join(",");
        }
      } else {
        return data?.["value"] ? data?.["value"] : "";
      }
    },
    /**
     * value(값) 세팅
     * @param {*} v
     */
    setValue(v, overwrite = false) {
      let selectedValue = "";
      if (props.isMulti && Array.isArray(v)) {
        selectedValue = v.map((item) => {
          return _keyValueForm(_getSelectedData(item));
        });
      } else {
        selectedValue = _keyValueForm(_getSelectedData(v));
      }
      _setValue(selectedValue, null, overwrite);
    },
    /**
     * 선택된 값에 대해 내부적인 값 포함 리턴
     * @returns
     */
    getSelectedData() {
      const { selectedData } = currState;
      return selectedData;
    },

    getDataSource() {
      const { dataSource } = currState;
      return dataSource;
    },

    /**
     * url options setting
     * @param {*} url
     * @param {*} method
     * @param {*} body
     */
    setDataBindingByAPI(url, method, body, _setVal = "", _callback) {
      _getDataFromAPIAndSet(url, method, body, _setVal, _callback);
    },
    /**
     * 첫번째 값 또는 기본 값으로 되돌리기
     */
    clear() {
      const { dataSource } = currState;
      const idx = props.defaultValue
        ? dataSource?.findIndex((x, idx) => {
            return (
              getItemValue(x, props.valueId) === props.defaultValue ||
              idx === Number(props.defaultValue)
            );
          })
        : 0;
      const firstRow = dataSource.length > 0 ? dataSource[idx] : null;
      const firstRowValue = _keyValueForm(firstRow);

      setCurrState((prev) => ({
        ...prev,
        selectedValue: firstRowValue,
        selectedData: firstRow,
        disabled: props.disabled,
      }));
    },
    /**
     * data binding
     * @param {*} source
     * * @param {*} _setVal
     * @param {*} callback
     */
    setDataBinding(source, _setVal, _callback) {
      _setDataBinding(source, _setVal, _callback);
    },
    /**
     * enable
     */
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    /**
     * disable
     */
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    async setSort(v) {
      _setSort(v);
    },

    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "SelectField: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  // url이 지정된 경우, API를 통해 데이터 가져와 바인딩
  const _getDataFromAPIAndSet = async (
    url,
    method,
    body,
    _setVal = "",
    _callback
  ) => {
    const response = await request(
      url,
      { method: method ?? "GET", ...(body && url ? { body: body } : body) },
      { showConfirm: false, showLoad: false }
    );
    if (response) {
      _setDataBinding(
        url ? response : response.data[props.respName],
        _setVal,
        _callback
      );
    }
  };

  useEffect(() => {
    if (props.dataSource) {
      _setDataBinding(props.dataSource);
      return;
    }

    // Combobox 렌더링 후, 데이터 바인딩
    // 공통코드 데이터인 경우, LocalStorage에서 가져와 바인딩
    const getCommonCodesAndSet = async (parentCodeId) => {
      const codes = await getChildrenCommonCode(parentCodeId);
      if (codes?.length > 0) {
        _setDataBinding(codes);
      }
      return;
    };
    if (props.parentCodeId) {
      getCommonCodesAndSet(props.parentCodeId);
      return;
    }

    if (props.url || (!props.url && props.apiBody)) {
      _getDataFromAPIAndSet(props.url, props.apiMethod, props.apiBody);
    }
  }, []);

  /**
   *
   * @param {*} source
   * @param {string | number | string[] | number[] | undefined} defaultValue 기본값 0 index 선택
   * @param {*} callback
   */
  const _setDataBinding = (source, setVal, callback) => {
    let data = normalizeDataSource(source);
    if (props.sort) _sorting(data, props.sort);
    if (props.addAll) data = _addAll(data);
    if (props.addBlank) data = _addBlank(data);
    if (props.addText) data = _addText(data, props.addText);

    // BindingData labelId로 중복 제거
    if (props.removeDuplicateValue.length > 0) {
      data = _removeDup(data, props.removeDuplicateValue);
    }

    // 기본 선택 배열 초기화 multi
    const defaultSelected = [];

    // 기본 선택 value 우선 순위
    // 1 매개 변수 호출
    // 2 Prop defaultValue
    // 3 0번째 index
    const defaultValue = setVal ?? props.defaultValue;

    if (Array.isArray(defaultValue)) {
      // 기본 선택 값이 배열인 경우
      defaultValue.forEach((value) => {
        // 배열 중에 Key 먼저 Search 이후 index 로 반환
        const findItem =
          data.find((_item) => getItemValue(_item, props.valueId) === value) ??
          data[+value];

        // null 처리
        findItem && defaultSelected.push(findItem);
      });
    } else {
      // Key 값으로 찾아보고 없으면 Index 로
      const findItem =
        data.find((_item) => getItemValue(_item, props.valueId) === defaultValue) ??
        data[+defaultValue];

      // null 처리
      findItem && defaultSelected.push(findItem);
    }

    // Multi 가 아닌경우 0번째
    const selectedData =
      defaultSelected.length > 0
        ? props.isMulti
          ? defaultSelected
          : defaultSelected[0]
        : null;

    // 선택된 옵션 생성
    const selectedValue = props.isMulti
      ? selectedData?.map(_keyValueForm)
      : _keyValueForm(selectedData);

    // 선택할 옵션 생성
    const options = data.map(_keyValueForm);

    let multiBlankChk = false;
    if (props.isMulti && !selectedData?.length) multiBlankChk = true;

    setCurrState((prev) => ({
      ...prev,
      selectedValue: multiBlankChk ? [] : selectedValue,
      selectedData: multiBlankChk ? [] : selectedData,
      dataSource: data,
      options: options,
    }));

    if (callback) callback();
  };

  const _addBlank = (data = []) => {
    const blank = {
      [props.valueId]: "",
      [props.labelId]: "",
      ord: 0,
    };

    return [blank, ...data];
  };

  const _addAll = (data = []) => {
    const all = {
      [props.valueId]: "",
      [props.labelId]: "전체",
      ord: 0,
    };

    return [all, ...data];
  };
  const _addText = (data = [], text) => {
    const blank = {
      [props.valueId]: "",
      [props.labelId]: text,
      ord: 0,
    };

    return [blank, ...data];
  };

  /**
   * e : change value
   * action : clear or remove-item.. etc.
   * overwrite : whether to overwrite the existing data
   * @param {*} e
   * @param {*} action
   * @param {*} hardCover
   */
  const _setValue = (e, action, overwrite = false) => {
    const { value } = action?.option ?? e ?? { value: null };
    const { dataSource } = currState;
    const found = dataSource?.find((data) => getItemValue(data, props.valueId) == value);

    if (props.isMulti) {
      const newData = _dataForm(currState.selectedValue, e, action, overwrite);

      setCurrState((prev) => ({
        ...prev,
        selectedValue: newData,
        selectedData: found,
      }));
    } else {
      setCurrState((prev) => ({
        ...prev,
        selectedValue: e,
        selectedData: found,
      }));
    }

    if (props.onChange) {
      props.onChange(e, found);
    }

    if (event?.key === "Enter") {
      if (props.onEnterPress) {
        props.onEnterPress(event, e);
      }
    } else {
      if (props.onKeyUp) {
        props.onKeyUp(event, e);
      }
    }
  };

  const _keyValueForm = (param) => {
    if (!param) return {};
    return { value: getItemValue(param, props.valueId), label: getItemValue(param, props.labelId) };
  };

  const _onChange = (e, action) => {
    _setValue(e, action);
  };

  const colourStyles = {
    menu: (provided) => ({ ...provided, zIndex: 999 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 10050 }),
    option: (style) => ({ ...style, minHeight: 25 }),
    singleValue: (provided) => {
      return { ...provided, color: "#999" };
    },
  };

  const _dataForm = (selectedValue, eventValue, action, overwrite) => {
    let newData = _rtnArrayData(selectedValue);
    const pushData = _rtnArrayData(eventValue);
    let rtnArr = [];

    if (overwrite) {
      newData = pushData;
    } else {
      pushData?.forEach((x) => {
        newData.push(x);
      });
    }

    const _item = new Set(newData?.map(JSON.stringify));
    rtnArr = Array.from(_item).map(JSON.parse);

    // 단일, 전체삭제 로직( 단일 삭제 시 index, 전체 삭제 시 array = [])
    if (action?.action === "remove-value") {
      rtnArr = rtnArr?.filter((item) => {
        return JSON.stringify(item) !== JSON.stringify(action.removedValue);
      });
    } else if (action?.action === "clear") {
      rtnArr = [];
    }

    return rtnArr;
  };

  const _rtnArrayData = (str) => {
    if (str === "") {
      return [];
    } else if (Array.isArray(str)) {
      return str;
    } else {
      try {
        return [str];
      } catch (error) {
        return [];
      }
    }
  };

  /**
   * - selected data info
   * - valueId 기준으로 dataSource 에서 조회
   * - 없으면 value 를 index 값으로 return
   * @param {string | number} value
   * @returns
   */
  const _getSelectedData = (value) => {
    const { dataSource } = currState;
    return (
      dataSource?.find((item) => getItemValue(item, props.valueId) === value) ??
      dataSource[+value] ??
      {}
    );
  };

  const normalizeDataSource = (source) => {
    return normalizeArrayData(source);
  };

  const getItemValue = (item, key) => {
    return getValueByKey(item, key);
  };

  /**
   * 키 입력 이벤트
   */
  const _onKeyDown = (e) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  /**
   * sorting
   * @param {*} codes
   * @param {*} v
   * @returns
   */
  const _sorting = async (codes = [], v) => {
    if (!codes.length || !v?.field) return codes;

    const { field: key, order = "asc" } = v;
    const isValueNumeric = codes[0][key] !== "" && !isNaN(codes[0][key]);

    const compare = (a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      if (isValueNumeric) {
        return aValue - bValue;
      } else {
        return aValue.localeCompare(bValue);
      }
    };

    codes.sort((a, b) => (order === "desc" ? compare(b, a) : compare(a, b)));

    return codes;
  };

  /**
   * sorting
   * @param {*} v
   */
  const _setSort = async (v) => {
    const { dataSource } = currState;
    const _data = _.cloneDeep(dataSource);

    _sorting(_data, v).then((codes) => {
      if (props.removeDuplicateValue.length > 0) {
        codes = _removeDup(codes, props.removeDuplicateValue);
      }
      const options = _rtnOption(codes);

      setCurrState((prev) => ({
        ...prev,
        dataSource: codes,
        options: options,
      }));
    });
  };

  return (
    // translate="no" is load-bearing, not cosmetic. react-select renders an aria-live
    // region (LiveRegion -> A11yText: <span><span>...text...</span></span>) that it
    // rewrites on every focus, keystroke and menu toggle. Edge/Chrome page translation
    // replaces those text nodes with <font> wrappers, so React's next update calls
    // removeChild on a node that is no longer its child and the whole screen dies with
    // "NotFoundError: Failed to execute 'removeChild' on 'Node'". The region is not in
    // react-select's `components` map (renderLiveRegion() is hardcoded), so the only
    // reachable guard is the container -- translate is inherited, so this covers it.
    // Value and placeholder text stay untranslated by design: they are text nodes React
    // swaps on selection too, so exempting them would just move the crash.
    <div
      translate="no"
      className={`${props.controlWidth ?? ""} ${props.className ?? ""}`}
      style={{ width: "100%" }}
    >
      <Select
        className={`${styles.e3Select} ${styles.e3Select_x}`}
        styles={colourStyles}
        ref={refSelect}
        placeholder={currState.placeholder}
        isClearable={currState.isClearable}
        isSearchable={currState.isSearchable}
        onChange={_onChange}
        onKeyDown={_onKeyDown}
        isDisabled={currState.disabled}
        options={currState.options}
        value={currState.selectedValue}
        isMulti={props.isMulti}
        defaultValue={currState.selectedValue}
        menuPlacement={props.menuPlacement}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        defaultMenuIsOpen={props.defaultMenuIsOpen}
        // server-side search: the caller fetches on input and passes filterOption={null}.
        // Left undefined, react-select keeps its own in-memory filter.
        onInputChange={props.onInputChange}
        filterOption={props.filterOption}
        {...(props.maxMenuHeight > -1 && {
          maxMenuHeight: props.maxMenuHeight,
        })}
      />
    </div>
  );
});

const SelectFieldPropTypes = {
  removeDuplicateValue: PropTypes.string,
  addBlank: PropTypes.bool,
  addAll: PropTypes.bool,
  valueId: PropTypes.string,
  labelId: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onEnterPress: PropTypes.func,
  onKeyUp: PropTypes.func,
  onInputChange: PropTypes.func,
  filterOption: PropTypes.func,
  parentCodeId: PropTypes.string,
  url: PropTypes.string,
  apiMethod: PropTypes.string,
  apiBody: PropTypes.object,
  dataSource: PropTypes.array,
  controlWidth: PropTypes.string,
  defaultValue: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
  ]),
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  defaultMenuIsOpen: PropTypes.bool,
  maxMenuHeight: PropTypes.number,
  respName: PropTypes.string,
  sort: PropTypes.object,
  /**
   * className
   */
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  hidden: PropTypes.bool,
};

SelectField.propTypes = SelectFieldPropTypes;
SelectField.defaultProps = {
  removeDuplicateValue: "",
  addBlank: false,
  addAll: false,
  valueId: "codeId",
  labelId: "codeName",
  disabled: false,
  onChange: null,
  onKeyDown: null,
  onEnterPress: null,
  onKeyUp: null,
  parentCodeId: null,
  url: null,
  apiMethod: null,
  apiBody: null,
  dataSource: null,
  controlWidth: null,
  defaultValue: undefined,
  isClearable: true,
  isSearchable: true,
  placeholder: "",
  isMulti: false,
  defaultMenuIsOpen: false,
  maxMenuHeight: -1,
  respName: "result",
  sort: null, // ex) { field: 'ord', order: 'asc' }
  className: null,
  hidden: false,
};
SelectField.displayName = "SelectField";
export default SelectField;
